import * as D from '@radix-ui/react-dialog'
import { PanelTop, X } from 'lucide-react'
import type { FormEventHandler, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

export type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full'

const SIZES: Record<ModalSize, string> = {
  xs: 'max-w-sm',
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  '2xl': 'max-w-6xl',
  full: 'max-w-none h-[calc(100dvh-2rem)]',
}

export type ModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  description?: ReactNode
  icon?: ReactNode
  tone?: 'default' | 'destructive'
  size?: ModalSize
  footer?: ReactNode
  onSubmit?: FormEventHandler<HTMLFormElement>
  variant?: 'default' | 'alert' | 'bare'
  dismissible?: boolean
  className?: string
  bodyClassName?: string
  children?: ReactNode
}

function CloseButton({ className }: { className?: string }) {
  const { t } = useTranslation()
  return (
    <D.Close
      className={cn(
        'flex size-8 shrink-0 items-center justify-center rounded-lg border bg-card text-muted-foreground shadow-xs transition-all hover:border-brand-blue/30 hover:bg-brand-blue/[0.06] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 active:scale-95',
        className,
      )}
      aria-label={t('common.close')}
    >
      <X className="size-4" />
    </D.Close>
  )
}

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  icon,
  tone = 'default',
  size = 'md',
  footer,
  onSubmit,
  variant = 'default',
  dismissible = true,
  className,
  bodyClassName,
  children,
}: ModalProps) {
  const hasBody = children !== undefined && children !== null && children !== false
  const block = (e: Event) => {
    if (!dismissible) e.preventDefault()
  }

  const content =
    variant === 'bare' ? (
      <>
        <D.Title className="sr-only">{title}</D.Title>
        {description ? <D.Description className="sr-only">{description}</D.Description> : null}
        {children}
        {dismissible ? <CloseButton className="absolute right-3 top-3" /> : null}
      </>
    ) : variant === 'alert' ? (
      <>
        <div className="flex flex-col items-center px-6 pt-7 text-center">
          {icon ? (
            <span
              className={cn(
                'mb-3 flex size-11 shrink-0 items-center justify-center rounded-full ring-4 [&_svg]:size-5 sm:mb-4 sm:size-14 sm:ring-[6px] sm:[&_svg]:size-6',
                tone === 'destructive' ? 'bg-destructive/12 text-destructive ring-destructive/[0.06]' : 'bg-primary/12 text-primary ring-primary/[0.06]',
              )}
              aria-hidden
            >
              {icon}
            </span>
          ) : null}
          <D.Title className="text-lg font-semibold leading-snug tracking-tight">{title}</D.Title>
          {description ? <D.Description className="mt-2 text-sm leading-relaxed text-muted-foreground">{description}</D.Description> : null}
        </div>
        {hasBody ? <div className={cn('scrollbar-none min-h-0 flex-1 overflow-y-auto px-6 pt-4', bodyClassName)}>{children}</div> : null}
        {footer ? <footer className="flex shrink-0 gap-2.5 px-6 pb-6 pt-6 [&>*]:flex-1">{footer}</footer> : null}
        {dismissible ? <CloseButton className="absolute right-3 top-3" /> : null}
      </>
    ) : (
      <>
        <header
          className={cn(
            'flex shrink-0 items-center gap-3.5 bg-primary/[0.06] px-5 py-4 dark:bg-muted/20',
            (hasBody || footer) && 'border-b',
          )}
        >
          <span
            className={cn(
              'flex size-8 shrink-0 items-center justify-center rounded-lg border [&_svg]:size-4 sm:size-10 sm:rounded-xl sm:[&_svg]:size-5',
              tone === 'destructive' ? 'border-destructive/15 bg-destructive/10 text-destructive' : 'border-primary/15 bg-primary/10 text-primary dark:bg-primary/15',
            )}
            aria-hidden
          >
            {icon ?? <PanelTop />}
          </span>
          <div className="min-w-0 flex-1">
            <D.Title className="text-base font-semibold leading-snug tracking-tight">{title}</D.Title>
            {description ? <D.Description className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">{description}</D.Description> : null}
          </div>
          {dismissible ? <CloseButton /> : null}
        </header>
        {hasBody ? <div className={cn('scrollbar-none min-h-0 flex-1 overflow-y-auto px-5 py-4', bodyClassName)}>{children}</div> : null}
        {footer ? <footer className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t bg-muted/40 px-5 py-3">{footer}</footer> : null}
      </>
    )

  return (
    <D.Root open={open} onOpenChange={(next) => (next || dismissible ? onOpenChange(next) : undefined)}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-50 animate-overlay-in bg-black/60 backdrop-blur-[3px] dark:bg-black/70" />
        <D.Content
          {...(description ? {} : { 'aria-describedby': undefined })}
          onEscapeKeyDown={block}
          onPointerDownOutside={block}
          onInteractOutside={block}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 animate-modal-in flex-col overflow-hidden rounded-2xl border bg-popover text-popover-foreground shadow-[0_24px_64px_-16px_color-mix(in_oklab,var(--shadow-color)_35%,transparent)] ring-1 ring-black/[0.03] focus:outline-none dark:shadow-[0_24px_64px_-16px_color-mix(in_oklab,var(--black)_70%,transparent)] dark:ring-white/[0.04]',
            SIZES[size],
            className,
          )}
        >
          {onSubmit ? (
            <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
              {content}
            </form>
          ) : (
            content
          )}
        </D.Content>
      </D.Portal>
    </D.Root>
  )
}
